import { HorizontalTabs } from "@/components/HorizontalTabs";
import { usePageTitle } from "@/hooks/usePageTitle";
import {
  createContext,
  useState,
  type Dispatch,
  type SetStateAction,
} from "react";
import { TeamFormationForm } from "./_components/team-formation-form";
import { CreateTeamForm } from "./_components/create-team-form";
import { useNavigate, useParams } from "react-router-dom";
import { usePermissions } from "@/hooks/usePermissions";
import { PLATFORM_SECTION_ENUM } from "@/utils/enums";
import { AccessDenied } from "@/components/permission-guard";
import { Loader as LoaderIcon } from "lucide-react";
import { PATHS } from "@/routes/paths";
import { useTranslation } from "react-i18next";

// eslint-disable-next-line
export enum TEAM_STEPS {
  BasicInformation = "basic-information",
  TeamFormation = "team-formation",
}

// eslint-disable-next-line
export const TeamFormationStepsContext = createContext<{
  currentStep: TEAM_STEPS;
  setStep: Dispatch<SetStateAction<TEAM_STEPS>> | null;
  resetStep: () => void;
}>({
  currentStep: TEAM_STEPS.BasicInformation,
  setStep: null,
  resetStep: () => {},
});

export const CreateTeams = () => {
  const { t } = useTranslation(["teams"]);
  const { teamId } = useParams<{ teamId: string }>();
  const isUpdating = Boolean(teamId);
  usePageTitle(
    isUpdating ? t("teams:form.edit_title") : t("teams:form.create_title"),
  );
  const navigate = useNavigate();
  const { can, isLoading: isPermissionsLoading } = usePermissions();

  const [currentStep, setStep] = useState(TEAM_STEPS.BasicInformation);
  const stepOrder = [TEAM_STEPS.BasicInformation, TEAM_STEPS.TeamFormation];
  const currentIndex = stepOrder.indexOf(currentStep);

  if (isPermissionsLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <LoaderIcon className="animate-spin text-primary" />
      </div>
    );
  }

  if (isUpdating && !can(PLATFORM_SECTION_ENUM.TEAMS, "edit")) {
    return (
      <AccessDenied
        description={t("teams:form.access_denied_edit")}
        onBack={() => navigate(PATHS.COMPANY.COMPANY_TEAMS_PATHS.ROOT)}
      />
    );
  }

  if (!isUpdating && !can(PLATFORM_SECTION_ENUM.TEAMS, "create")) {
    return (
      <AccessDenied
        description={t("teams:form.access_denied_create")}
        onBack={() => navigate(PATHS.COMPANY.COMPANY_TEAMS_PATHS.ROOT)}
      />
    );
  }

  const tabsConfig = [
    {
      value: TEAM_STEPS.BasicInformation,
      label: t("teams:form.tab_basic_information"),
      content: <CreateTeamForm teamId={teamId} isUpdating={isUpdating} />,
    },
    {
      value: TEAM_STEPS.TeamFormation,
      label: t("teams:form.tab_team_formation"),
      content: <TeamFormationForm teamId={teamId} />,
    },
  ].map((tab, index) => ({
    ...tab,
    // Allow navigation to next steps when updating
    disabled: isUpdating ? false : index > currentIndex,
  }));

  const resetStep = () => setStep(TEAM_STEPS.BasicInformation);

  return (
    <TeamFormationStepsContext.Provider
      value={{
        currentStep: currentStep,
        setStep: setStep,
        resetStep,
      }}
    >
      <HorizontalTabs
        tabConfig={tabsConfig}
        handleTabChange={(newValue) => setStep(newValue as TEAM_STEPS)}
      />
    </TeamFormationStepsContext.Provider>
  );
};
